import { ASR_ENGINE_WORKER, ASR_ENGINE_MOONSHINE, getAsrEngine } from '../../services/asr'
import { isSupported as isMoonshineSupported } from '../../services/asrMoonshine'

export var ASR_ENGINES = [
  { value: ASR_ENGINE_WORKER, label: 'Whisper (cloud)' }
].concat(isMoonshineSupported() ? [{ value: ASR_ENGINE_MOONSHINE, label: 'Local model' }] : [])

var ASR_ENGINE_KEY = 'ezshadowAsrEngine'

export default {
  namespaced: true,

  state: {
    settingsLoaded: false,  // New property to track loading state
    asrEngine: ASR_ENGINE_WORKER,
    availableLanguages: [
      { code: 'en', name: 'English', speechCode: 'en-US' },
      { code: 'vi', name: 'Vietnamese', speechCode: 'vi-VN' },
      { code: 'ja', name: 'Japanese', speechCode: 'ja-JP' },
      { code: 'ko', name: 'Korean', speechCode: 'ko-KR' },
      { code: 'es', name: 'Spanish', speechCode: 'es-ES' },
      { code: 'fr', name: 'French', speechCode: 'fr-FR' },
      { code: 'de', name: 'German', speechCode: 'de-DE' },
      { code: 'zh', name: 'Chinese (Simplified)', speechCode: 'zh-CN' },
      { code: 'ru', name: 'Russian', speechCode: 'ru-RU' }
    ],
    targetLanguage: { code: 'en', name: 'English', speechCode: 'en-US' },
    sourceLanguage: { code: 'vi', name: 'Vietnamese', speechCode: 'vi-VN' },
    showSettingsModal: false,
    // 'drill' | 'account'
    settingsTab: 'drill',
    // Cut-down "pick your languages" form.
    firstRun: false,
    tutorialAfterFirstRun: false,
    showTutorial: false,
    showOverlay: true,
    showShareSheet: false,
    sharePayload: null,
    // { type: 'bug'|'idea'|'other', context } while the feedback sheet is open (STORY-016).
    feedback: null
  },

  mutations: {
    SET_SETTINGS_LOADED(state, isLoaded) {
      state.settingsLoaded = isLoaded;
    },

    SET_SETTINGS_TAB(state, tab) {
      state.settingsTab = tab === 'account' ? 'account' : 'drill';
    },

    SET_FIRST_RUN(state, v) {
      state.firstRun = !!v;
    },

    SET_TUTORIAL_AFTER_FIRST_RUN(state, v) {
      state.tutorialAfterFirstRun = !!v;
    },

    SET_SHOW_TUTORIAL(state, v) {
      state.showTutorial = !!v;
    },

    SET_ASR_ENGINE(state, engine) {
      state.asrEngine = engine;
    },
    
    SET_TARGET_LANGUAGE(state, language) {
      state.targetLanguage = language;
    },
    
    SET_SOURCE_LANGUAGE(state, language) {
      state.sourceLanguage = language;
    },
    
    TOGGLE_SETTINGS_MODAL(state, isVisible) {
      state.showSettingsModal = isVisible !== undefined ? isVisible : !state.showSettingsModal;
    },
    
    TOGGLE_OVERLAY(state) {
      state.showOverlay = !state.showOverlay;
    },

    SET_SHARE_PAYLOAD(state, payload) {
      state.sharePayload = payload || null;
    },

    TOGGLE_SHARE_SHEET(state, isVisible) {
      state.showShareSheet = isVisible !== undefined ? isVisible : !state.showShareSheet;
    },

    SET_FEEDBACK(state, feedback) {
      state.feedback = feedback || null;
    }
  },
  
  actions: {
    loadSavedSettings({ commit, dispatch, state }) {
      return new Promise((resolve) => {
      commit('SET_SETTINGS_LOADED', false);
      
      const savedState = localStorage.getItem('shadowingAppState');
      if (savedState) {
        try {
          const state = JSON.parse(savedState);
          
          if (state.videoId) {
            dispatch('player/setVideoId', state.videoId, { root: true });
          }
          
          if (state.startTime !== undefined) {
            dispatch('player/setStartTime', parseFloat(state.startTime) || 0, { root: true });
          }
          
          if (state.sourceLanguage) {
            commit('SET_SOURCE_LANGUAGE', state.sourceLanguage);
          }
          
          if (state.targetLanguage) {
            commit('SET_TARGET_LANGUAGE', state.targetLanguage);
          }
        } catch (e) {
          console.error('Failed to parse saved state:', e);
        }
      }

      const savedRate = parseFloat(localStorage.getItem('ezshadowPlaybackRate'));
      if (savedRate && savedRate > 0 && savedRate <= 2) {
        dispatch('player/setPlaybackRate', savedRate, { root: true });
      }

      commit('SET_ASR_ENGINE', getAsrEngine());

      commit('SET_SETTINGS_LOADED', true);
      resolve();
      });
    },
    
    // Does not close the modal: LearningScreen's URL sync calls this too. See saveAndClose.
    saveSettings({ state, rootState }) {
      const stateData = {
        videoId: rootState.player.videoId,
        startTime: rootState.player.startTime,
        targetLanguage: state.targetLanguage,
        sourceLanguage: state.sourceLanguage,
        savedAt: new Date().getTime()
      };

      localStorage.setItem('shadowingAppState', JSON.stringify(stateData));

      return Promise.resolve();
    },
    
    setAsrEngine({ commit }, engine) {
      if (engine !== ASR_ENGINE_WORKER && engine !== ASR_ENGINE_MOONSHINE) return;
      commit('SET_ASR_ENGINE', engine);
      localStorage.setItem(ASR_ENGINE_KEY, engine);
    },

    updateVideoId({ dispatch }, videoId) {
      dispatch('player/setVideoId', videoId, { root: true });
    },
    
    updateLanguages({ commit }, { sourceLanguage, targetLanguage }) {
      if (sourceLanguage) {
        commit('SET_SOURCE_LANGUAGE', sourceLanguage);
      }

      if (targetLanguage) {
        commit('SET_TARGET_LANGUAGE', targetLanguage);
      }
      return Promise.resolve();
    },
    
    saveAndClose({ dispatch }) {
      dispatch('saveSettings');
      dispatch('closeModal');
    },

    // Shell UI (settings, tutorial, share sheet) is rendered by App.vue above the
    // router, so its state lives here to survive route changes.

    openSettings({ commit }, tab) {
      commit('SET_FIRST_RUN', false);
      commit('SET_SETTINGS_TAB', tab || 'drill');
      commit('TOGGLE_SETTINGS_MODAL', true);
    },

    // First visit: languages-only settings, then the tutorial on close.
    openFirstRunSettings({ commit }, { withTutorial }) {
      commit('SET_FIRST_RUN', true);
      commit('SET_TUTORIAL_AFTER_FIRST_RUN', !!withTutorial);
      commit('TOGGLE_SETTINGS_MODAL', true);
    },

    closeModal({ commit, state }) {
      const wasFirstRun = state.firstRun;
      const runTutorial = state.tutorialAfterFirstRun;
      commit('TOGGLE_SETTINGS_MODAL', false);
      commit('SET_FIRST_RUN', false);
      commit('SET_TUTORIAL_AFTER_FIRST_RUN', false);
      if (wasFirstRun && runTutorial) {
        commit('SET_SHOW_TUTORIAL', true);
      }
    },

    openTutorial({ commit }) {
      commit('SET_SHOW_TUTORIAL', true);
    },

    closeTutorial({ commit }) {
      commit('SET_SHOW_TUTORIAL', false);
    },

    // payload: { videoId, start, lang, motherTongue, score, name, sentence, canAttachVoice }
    openShareSheet({ commit }, payload) {
      if (!payload || !payload.videoId) return;
      commit('SET_SHARE_PAYLOAD', payload);
      commit('TOGGLE_SHARE_SHEET', true);
    },

    closeShareSheet({ commit }) {
      commit('TOGGLE_SHARE_SHEET', false);
      commit('SET_SHARE_PAYLOAD', null);
    },

    openFeedback({ commit }, { type, context }) {
      commit('SET_FEEDBACK', { type: type, context: context });
    },

    closeFeedback({ commit }) {
      commit('SET_FEEDBACK', null);
    }
  }
}
